import * as Sentry from "@sentry/nextjs"
import { Spinner, Tooltip } from "flowbite-react"
import moment, { Moment } from "moment"
import { useRouter } from "next/router"
import React, { useEffect, useState } from "react"
import useSWR from "swr"

import { CaretDownIcon, CloseIcon, LockIcon } from "@assets/icons"
import DangerouslyHTMLTextPriview from "@components/elements/DangerouslyHTMLTextPriview"
import useAuth from "@hooks/useAuth"
import BookingSwLogo from "@icons/BookingSwLogo"
import * as ProductServices from "@mono/essentials/services/ProductServices"
import * as TenantServices from "@mono/essentials/services/TenantServices"
import { IDomain } from "@mono/essentials/types/UserTypes"
import { getRGBColor } from "@mono/essentials/utils/Common"
import { storeXDomainTimezone } from "@mono/essentials/utils/TenantHelper"

import Preview from "./components/Preview"

const Integrated_widget = () => {
  const router = useRouter()
  const { query, isReady } = router

  if (!isReady) {
    return (
      <div className={"flex items-center justify-center h-screen"}>
        <Spinner size={"lg"} />
      </div>
    )
  }

  const widget_details = (query as any) ?? null

  const {
    data: publicDomainDetails,
    isValidating: isDomainDetailsValidating,
    isLoading: isDomainDetailsLoading = true,
  } = useSWR<IDomain>(
    `publicDomainDetails-${widget_details}`,
    async () => {
      const { data } = await TenantServices.getDomainBasicDetails()
      if (data?.timezone_iso3) {
        moment.tz.setDefault(data.timezone_iso3)
        storeXDomainTimezone(data.timezone_iso3)
      }
      return data
    },
    {
      revalidateOnFocus: false,
    }
  )

  const { data: selectedProductDetail, isLoading: selectedProductDetailLoading = true } = useSWR(
    widget_details.product ? `fetchProductList-${widget_details.product}` : null,
    async () => {
      try {
        const { data } = await ProductServices.getProductInfoById(widget_details.product, {}, true)
        return data
      } catch (e) {
        Sentry.captureException(e)
        console.error({ e })
      }
    },
    {
      revalidateOnFocus: false,
    }
  )

  return (
    <>
      {widget_details?.widget_type === "custom_widget" && !widget_details?.nextStep ? (
        <div
          className={
            "flex justify-center w-7/12 mx-auto border border-gray-200 rounded items-flex-start my-11 bg-whiteBlack-100"
          }
        >
          <div className={"flex w-full"}>
            <div
              className={"flex items-center justify-between p-4 px-8 border-b border-neutral-200"}
            >
              <Tooltip
                content={
                  "Your info will be transferred securely over a 2048-bit encrypted connection."
                }
                trigger={"click"}
                className={"text-xs w-52"}
              >
                <div
                  className={
                    "text14Medium shadow-[0px 2px 5px rgba(42, 93, 227, 0.3)] flex cursor-pointer flex-row items-center justify-center gap-2 rounded-md bg-green-800 p-2.5 text-whiteBlack-100"
                  }
                >
                  <LockIcon className={"mr-2 h-4.5 w-4.5"} />
                  Secured
                  <CaretDownIcon className={"ml-2 h-4.5 w-4.5"} />
                </div>
              </Tooltip>
              <CloseIcon className={"ml-2 h-4.5 w-4.5 cursor-pointer"} />
            </div>
            {isDomainDetailsLoading || selectedProductDetailLoading || isDomainDetailsValidating ? (
              <div className={"h-[350px]"}>
                <div className={"flex items-center justify-center h-full text-center"}>
                  <Spinner size={"md"} light />
                </div>
              </div>
            ) : (
              <div className={`p-5 md:flex justify-center items-center overflow-auto`}>
                {selectedProductDetail &&
                  widget_details.widgetType !== "custom_widget" &&
                  widget_details.widget_type !== "product_details" && (
                    <>
                      <div className={"flex items-center justify-center p-5"}>
                        <div className={"p-6"}>
                          <div className={"flex flex-col gap-2"}>
                            <div className={"text-gray-500 text16Medium"}>You're Booking</div>
                            <div className={"flex items-center gap-1"}>
                              {selectedProductDetail &&
                              selectedProductDetail?.mediaFiles &&
                              selectedProductDetail?.mediaFiles?.length > 0 ? (
                                <img
                                  src={selectedProductDetail?.mediaFiles[0]?.public_url}
                                  className={"h-[70px] w-[70px] rounded-lg"}
                                />
                              ) : (
                                <div
                                  className={
                                    "relative flex h-full min-h-[70px] w-[70px] cursor-pointer flex-row items-end rounded-lg bg-imagePlaceholderBackground bg-cover bg-center shadow-sm "
                                  }
                                >
                                  <div
                                    className={
                                      "absolute left-1/2 top-1/2 h-11 w-[30px] -translate-x-1/2 -translate-y-1/2 transform bg-transparent bg-imagePlaceholder bg-contain bg-center bg-no-repeat"
                                    }
                                  />
                                </div>
                              )}
                              <div className={"flex flex-col gap-2"}>
                                <span className={"text16Medium text-neutral-900"}>
                                  {selectedProductDetail?.name}
                                </span>
                                <DangerouslyHTMLTextPriview
                                  content={selectedProductDetail?.description?.toString() ?? ""}
                                  className={
                                    "overflow-hidden paragraph14Medium text-elipsis whitespace-nowrap text-neutral-600"
                                  }
                                />
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </>
                  )}
                <Preview
                  data={widget_details}
                  type={widget_details.widgetType}
                  isIntegratedPage
                  domainBasicDetails={publicDomainDetails}
                  nextStep={widget_details.nextStep}
                />
              </div>
            )}
            <div className={"p-4 border-t border-neutral-200"}>
              <div
                className={`flex flex-1 flex-row items-center justify-between bg-whiteBlack-100`}
              >
                <div className={"flex items-center"}>
                  <div className={"mr-2 border-r w-fit border-neutral-300"}>
                    <BookingSwLogo className={"mr-2"} />
                  </div>
                  <div className={"flex flex-col"}>
                    <div className={"paragraph13 text-neutral-1000"}>
                      Powered by{" "}
                      <span className={"text-primary-600 font-medium"}>
                        {publicDomainDetails?.company ?? ""}
                      </span>
                    </div>
                    <div
                      className={"text12Medium text-neutral-600"}
                    >{`All prices are in ${publicDomainDetails?.currency_iso3}`}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <>
          <>
            <style>
              .widget-theme-wrapper{" "}
              {`{${getRGBColor(widget_details?.theme_brand_color ?? "#2F69FE", "primary")}}`}
              .widget-theme-wrapper [type='tel']:focus
              {` {--tw-ring-color: var(--color-primary-400);}`}
            </style>
            <Preview
              data={widget_details}
              type={widget_details?.widgetType}
              isIntegratedPage
              domainBasicDetails={publicDomainDetails}
              nextStep={widget_details?.nextStep}
            />
          </>
        </>
      )}
    </>
  )
}

export default Integrated_widget
